import React from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { blogPostMap } from '../content/blogPosts';
import { blogPosts } from '../content/blogPosts';
import { getLocalizedBlogPosts, hasBlogTranslation } from '../content/blogTranslations';
import { linkFor, localizedPath, pageLanguage, urlLanguage } from '../content/publicLanguages';
import Seo from './Seo';

const formatDate = (date, language) =>
  new Intl.DateTimeFormat(language || 'en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }).format(new Date(date));

// Section anchors ("#check-where-the-yellowing-starts"): the table of contents links to them, and
// search engines can send readers straight to the part that answers their question.
export const anchorFor = (heading) => heading.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80) || 'section';

// Same topic first, then the most recent: internal links readers (and crawlers) can follow.
export const relatedPosts = (post, posts, count = 3) => {
  const others = posts.filter((p) => p.slug !== post.slug && p.status !== 'draft')
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
  return [...others.filter((p) => p.category === post.category), ...others.filter((p) => p.category !== post.category)]
    .slice(0, count);
};

const minutesIn = (readTime) => Number(/\d+/.exec(readTime || '')?.[0]) || null;

const BlogPost = () => {
  const { slug } = useParams();
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();
  const urlLang = urlLanguage(pathname);
  const language = pageLanguage(pathname, i18n);
  const localizedPosts = getLocalizedBlogPosts(blogPosts, language);
  const post = localizedPosts.find((item) => item.slug === slug) || blogPostMap[slug];

  if (!post || post.status === 'draft') {
    return <Navigate to={linkFor('/blog', language)} replace />;
  }
  if (urlLang && !hasBlogTranslation(slug, urlLang)) {
    return <Navigate to={`/blog/${slug}`} replace />;   // no version in that language: the original
  }

  const anchors = new Map();
  const sections = post.sections.map((section) => {
    const base = anchorFor(section.heading);
    const n = (anchors.get(base) || 0) + 1;
    anchors.set(base, n);
    return { ...section, id: n > 1 ? `${base}-${n}` : base };
  });
  const minutes = minutesIn(post.readTime);
  const related = relatedPosts(post, localizedPosts);

  return (
    <div className="blog-post-page">
      <Seo
        title={post.title}
        description={post.description}
        canonicalPath={localizedPath(`/blog/${post.slug}`, urlLang)}
        type="article"
      />

      <div className="blog-post-shell">
        <Link to={linkFor('/blog', language)} className="blog-back-link">
          ← {t('public.blog.back_to_blog')}
        </Link>

        <article className="blog-post-article">
          <header className="blog-post-header">
            <span className="journal-category">{post.category}</span>
            <h1>{post.title}</h1>
            <p className="blog-post-description">{post.description}</p>
            <div className="blog-post-meta">
              <span>{t('public.blog.byline')}</span>
              <time dateTime={post.publishedAt}>{formatDate(post.publishedAt, language)}</time>
              {post.updatedAt && post.updatedAt !== post.publishedAt && (
                <span>{t('public.blog.updated', { date: formatDate(post.updatedAt, language) })}</span>
              )}
              {minutes && <span>{t('public.blog.read_time', { minutes })}</span>}
            </div>
          </header>

          {post.excerpt && (
            <aside className="blog-post-summary" aria-label={t('public.blog.in_short')}>
              <strong>{t('public.blog.in_short')}</strong>
              <p>{post.excerpt}</p>
            </aside>
          )}

          {sections.length >= 3 && (
            <nav className="blog-post-toc" aria-label={t('public.blog.in_this_article')}>
              <strong>{t('public.blog.in_this_article')}</strong>
              <ol>
                {sections.map((section) => (
                  <li key={section.id}><a href={`#${section.id}`}>{section.heading}</a></li>
                ))}
              </ol>
            </nav>
          )}

          <div className="blog-post-body">
            {sections.map((section) => (
              <section key={section.id} className="blog-post-section">
                <h2 id={section.id}>{section.heading}</h2>
                {section.paragraphs?.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                {section.bullets?.length ? (
                  <ul>
                    {section.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ))}
          </div>

          <div className="blog-post-cta">
            <h2>{t('public.blog.cta_title')}</h2>
            <p>{t('public.blog.cta_body')}</p>
            <Link to="/login" className="cta-primary">
              {t('public.blog.cta_button')} <span className="cta-arrow" aria-hidden="true">&rarr;</span>
            </Link>
          </div>

          {related.length > 0 && (
            <nav className="blog-post-related" aria-label={t('public.blog.related')}>
              <h2>{t('public.blog.related')}</h2>
              <ul>
                {related.map((item) => (
                  <li key={item.slug}>
                    <Link to={linkFor(`/blog/${item.slug}`, language)}>{item.title}</Link>
                    <span>{item.category}</span>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </article>
      </div>
    </div>
  );
};

export default BlogPost;
